import API from "./axios/API";

// The rollout APIs live under /api/v6, while the rest of this app uses /api/v2.
const v6BaseUrl = (process.env.REACT_APP_BASE_URL || "").replace(
  /\/api\/v\d+\/?$/,
  "/api/v6/",
);

// Dev only: REACT_APP_RELEASES_MOCK=random|manual runs the screen without a backend.
const request =
  process.env.NODE_ENV === "development" &&
  process.env.REACT_APP_RELEASES_MOCK
    ? require("./appReleases.mock").request
    : (config) =>
        API.request({ baseURL: v6BaseUrl, ...config }).then(unwrapEnvelope);

// The backend wraps responses as { success, data }. Unwrap object payloads; keep the
// envelope when data is an array so paginated responses still expose meta.
function unwrapEnvelope(response) {
  if (
    response &&
    response.data &&
    typeof response.data === "object" &&
    !Array.isArray(response.data)
  ) {
    return response.data;
  }
  return response;
}

const releaseUrl = (id, path = "") => `admin/pos-releases/${id}${path}`;

const toList = (response, key) => {
  const list = response[key] || response.data;
  return Array.isArray(list) ? list : [];
};

export const getReleases = async () => {
  const response = await request({ method: "get", url: "admin/pos-releases" });
  return { ...response, releases: toList(response, "releases") };
};

export const getRelease = (id) =>
  request({ method: "get", url: releaseUrl(id) });

export const getReleaseOutlets = async (id, { search = "", page = 1 } = {}) => {
  const response = await request({
    method: "get",
    url: releaseUrl(id, "/outlets"),
    params: { search, page },
  });
  const paging = response.meta || response;
  const outlets = toList(response, "outlets");
  return {
    outlets,
    page: paging.current_page || page,
    lastPage: paging.last_page || 1,
    perPage: paging.per_page || outlets.length,
    total: paging.total !== undefined ? paging.total : outlets.length,
  };
};

// PUT /outlets replaces the whole list, so the picker needs every page, not just the visible one.
export const getAllReleaseOutlets = async (id) => {
  const first = await getReleaseOutlets(id);
  let outlets = first.outlets;
  for (let page = 2; page <= first.lastPage; page++) {
    const next = await getReleaseOutlets(id, { page });
    outlets = outlets.concat(next.outlets);
  }
  return outlets;
};

export const getReleaseActivity = async (id) =>
  toList(
    await request({ method: "get", url: releaseUrl(id, "/activity") }),
    "activity",
  );

const postAction = (id, action, data) =>
  request({ method: "post", url: releaseUrl(id, `/${action}`), data });

export const startRelease = (id) => postAction(id, "start");
export const promoteRelease = (id, reason) =>
  postAction(id, "promote", reason ? { reason } : undefined);
export const pauseRelease = (id) => postAction(id, "pause");
export const resumeRelease = (id) => postAction(id, "resume");
export const rollbackRelease = (id, reason) =>
  postAction(id, "rollback", { reason });
export const approveRelease = (id) => postAction(id, "approve");
export const releaseToAll = (id) => postAction(id, "release-all");

export const setSafeToRollBack = (id, safe, reason) =>
  request({
    method: "put",
    url: releaseUrl(id, "/safe-to-roll-back"),
    data: { safe, reason },
  });

export const setReleaseOutlets = (id, outletIds) =>
  request({
    method: "put",
    url: releaseUrl(id, "/outlets"),
    data: { outlet_ids: outletIds },
  });

export const getHolds = async () =>
  toList(
    await request({ method: "get", url: "admin/pos-release-holds" }),
    "holds",
  );

export const addHold = (hold) =>
  request({ method: "post", url: "admin/pos-release-holds", data: hold });

export const removeHold = (id) =>
  request({ method: "delete", url: `admin/pos-release-holds/${id}` });
